// Landing page (/) — a grid of big square widgets, one per known plan, plus a "New plan" tile.
//
// The plan LIST comes from localStorage recents (this browser's opened plans); the server is
// only asked to fill in each card's numbers via GET /api/projects/meta. So the dashboard shows
// "plans I know about", not "every plan on the server" — and it works offline-ish (names from
// localStorage render immediately, stats fill in when the fetch lands).

import { listRecents, removeRecent, type RecentPlan } from "./recents";
import { relTime } from "../shared/format";

interface PlanMeta {
  guid: string;
  found: boolean;
  name?: string;
  version?: number;
  savedAt?: string | null;
  changes?: number;
  versions?: string[];
  tasks?: number | null;
  doneTasks?: number | null;
  days?: number | null;
  pct?: number | null;
  doneDays?: number | null; // finished estimate-days
  openDays?: number | null; // estimate-days still to do (doneDays + openDays == the weighted total)
  doneNoEst?: number;       // done tasks carrying no estimate — invisible to a day-weighted bar
  sections?: string[];
  moreSections?: number;
}

const grid = document.getElementById("grid") as HTMLElement;
const emptyHint = document.getElementById("emptyHint") as HTMLElement;

let metas = new Map<string, PlanMeta>();

/* ---------- data ---------- */

async function fetchMetas(guids: string[]): Promise<void> {
  if (!guids.length) return;
  try {
    const res = await fetch("/api/projects/meta?guids=" + encodeURIComponent(guids.join(",")));
    if (!res.ok) throw new Error("HTTP " + res.status);
    const body = await res.json();
    metas = new Map((body.plans as PlanMeta[]).map((p) => [p.guid, p]));
  } catch (e) {
    console.error("could not load plan metadata", e); // cards still render from localStorage
  }
}

// server savedAt is the truth for "when it changed"; the recents ts (last opened here) is the fallback
function changedAt(p: RecentPlan): number {
  const saved = metas.get(p.guid)?.savedAt;
  const t = saved ? Date.parse(saved) : NaN;
  return Number.isFinite(t) ? t : p.ts || 0;
}

/* ---------- tiles ---------- */

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

function newTile(): HTMLElement {
  const tile = el("button", "tile tile-new");
  tile.title = "Create a new plan on the server";
  tile.appendChild(el("span", "plus", "+"));
  tile.appendChild(el("span", "tile-new-label", "New plan"));
  tile.onclick = () => createPlan(tile);
  return tile;
}

async function createPlan(tile: HTMLButtonElement): Promise<void> {
  tile.disabled = true;
  tile.classList.add("busy");
  try {
    const res = await fetch("/api/projects", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // same starting shape the planner's "New plan" uses
      body: JSON.stringify({ data: { name: "Untitled project", showTags: true, showVersions: true, showDone: true, versions: [], tree: [] } }),
    });
    if (!res.ok) throw new Error("HTTP " + res.status);
    const body = await res.json();
    location.href = "/plan/" + body.guid;
  } catch (e: any) {
    tile.disabled = false;
    tile.classList.remove("busy");
    alert("Could not create a new plan: " + e.message);
  }
}

function planTile(p: RecentPlan, now: number): HTMLElement {
  const meta = metas.get(p.guid);
  const missing = meta ? !meta.found : false; // no meta yet (fetch failed/pending) → don't cry "missing"
  const name = (meta?.found && meta.name) || p.name || "Untitled plan";

  const tile = el("a", "tile tile-plan" + (missing ? " missing" : ""));
  tile.href = "/plan/" + p.guid;

  const head = el("div", "tile-head");
  head.appendChild(el("h2", "tile-name", name));
  tile.appendChild(head);

  // ✕ forget — inside the <a>, so stop the click from navigating
  const forget = el("button", "tile-x", "✕");
  forget.title = "Forget this plan (removes it from this list only — the plan stays on the server)";
  forget.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    removeRecent(p.guid);
    render();
  };
  tile.appendChild(forget);

  if (missing) {
    tile.appendChild(el("p", "tile-missing", "Not found on the server — it may have been deleted."));
    tile.appendChild(el("div", "tile-foot", "Opening it will start a fresh plan at this address."));
    return tile;
  }

  if (meta?.sections?.length) {
    const secs = el("ul", "tile-sections");
    meta.sections.forEach((s) => secs.appendChild(el("li", undefined, s)));
    if (meta.moreSections) secs.appendChild(el("li", "more", `+${meta.moreSections} more`));
    tile.appendChild(secs);
  }

  const stats = el("div", "tile-stats");
  if (meta && meta.tasks != null) {
    stats.appendChild(stat(String(meta.tasks), meta.tasks === 1 ? "task" : "tasks"));
    if (meta.days != null) stats.appendChild(stat(fmtDays(meta.days), "estimate"));
    if (meta.versions?.length) stats.appendChild(stat(String(meta.versions.length), meta.versions.length === 1 ? "version" : "versions"));
  }
  tile.appendChild(stats);

  if (meta && meta.pct != null && meta.tasks) tile.appendChild(progressBlock(meta));

  const foot = el("div", "tile-foot");
  const saved = meta?.savedAt || null;
  const rel = relTime(saved, now);
  const when = el("span", "when", rel ? "Changed " + rel : "Changed — unknown");
  if (saved) when.title = new Date(saved).toLocaleString();
  foot.appendChild(when);
  if (meta?.version) foot.appendChild(el("span", "vpill", "v" + meta.version));
  tile.appendChild(foot);

  return tile;
}

// The bar + % stay as they were; under them goes a line in plain days ("17d left · 13d of 30d"),
// because a percentage alone makes the reader do the arithmetic to answer "how much is left?".
function progressBlock(meta: PlanMeta): HTMLElement {
  const tasks = meta.tasks || 0;
  const doneTasks = meta.doneTasks || 0;
  const done = meta.doneDays ?? 0;
  const open = meta.openDays ?? 0;
  const total = done + open;
  // nothing estimated at all → a day-weighted bar is pinned at 0% and tells you nothing;
  // fall back to counting tasks (drawn hatched, and the note says so, so the unit is never a guess)
  const byTasks = total <= 0;
  const pct = byTasks ? (tasks ? Math.round((doneTasks / tasks) * 100) : 0) : (meta.pct ?? 0);

  const wrap = el("div", "prog");
  const row = el("div", "prog-row");
  const bar = el("div", "prog-bar");
  if (byTasks) {
    const hatch = el("div", "prog-hatch");
    hatch.style.width = pct + "%";
    bar.appendChild(hatch);
  } else {
    const fill = el("div", "prog-fill" + (pct === 100 ? " done" : ""));
    fill.style.width = pct + "%";
    bar.appendChild(fill);
    // Done tasks with no estimate weigh 0 days, so the day-weighted fill can't show them. Add a
    // hatched sliver sized by their share of the task COUNT (they have no days) — an indicator of
    // unmeasured progress, not a measurement. Capped at HALF the free space so it can never fill
    // the bar: days are still outstanding, and a full bar would read as "finished".
    const share = tasks ? ((meta.doneNoEst || 0) / tasks) * 100 : 0;
    const extra = Math.min(share, (100 - pct) / 2);
    if (extra >= 1) {
      const hatch = el("div", "prog-hatch");
      hatch.style.width = extra + "%";
      bar.appendChild(hatch);
    }
  }
  row.appendChild(bar);
  row.appendChild(el("span", "prog-pct", pct + "%"));
  wrap.appendChild(row);

  const note = el("div", "prog-note");
  if (byTasks) {
    note.appendChild(el("span", "lead", `${doneTasks} of ${tasks} tasks`));
    note.appendChild(el("span", undefined, "no estimates"));
  } else if (open <= 0) {
    note.appendChild(el("span", "lead", "All " + fmtDays(total) + " done"));
  } else {
    note.appendChild(el("span", "lead", fmtDays(open) + " left"));
    note.appendChild(el("span", undefined, fmtDays(done) + " of " + fmtDays(total)));
  }
  wrap.appendChild(note);

  const lines = byTasks
    ? [`${pct}% of tasks done (${doneTasks} of ${tasks}) — no estimates yet, so this counts tasks, not days`]
    : [`${fmtDays(done)} done · ${fmtDays(open)} left of ${fmtDays(total)} estimated`,
       `${doneTasks} of ${tasks} tasks done`];
  if (!byTasks && meta.doneNoEst) {
    lines.push(`${meta.doneNoEst} done task${meta.doneNoEst === 1 ? "" : "s"} carry no estimate (hatched — they add no days)`);
  }
  wrap.title = lines.join("\n");
  return wrap;
}

function stat(value: string, label: string): HTMLElement {
  const s = el("div", "stat");
  s.appendChild(el("strong", undefined, value));
  s.appendChild(el("span", undefined, label));
  return s;
}

// 12 → "12d", 0.5 → "0.5d"
function fmtDays(d: number): string {
  return (Math.round(d * 100) / 100) + "d";
}

/* ---------- render ---------- */

function render(): void {
  const now = Date.now();
  const plans = listRecents().slice().sort((a, b) => changedAt(b) - changedAt(a));
  grid.innerHTML = "";
  grid.appendChild(newTile());
  plans.forEach((p) => grid.appendChild(planTile(p, now)));
  emptyHint.hidden = plans.length > 0;
}

(async () => {
  render(); // paint immediately from localStorage…
  await fetchMetas(listRecents().map((p) => p.guid));
  render(); // …then again with the server's names, stats and timestamps
})();
